<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import VocabularyDialog from './VocabularyDialog.vue'
import { store } from '../store'

const props = defineProps({lesson: {type: Object, required: true}, target: {default: null}})
const popup = ref(null)
const query = ref('')
const selected = ref(null)
const selectionStyle = ref({})
const message = ref('')
const termPattern = /^[a-z]+(?:['’-][a-z]+)*(?:\s+[a-z]+(?:['’-][a-z]+)*)*$/i
let boundTarget = null

function validTerm(term) { return term.length <= 100 && term.split(/\s+/).length <= 12 && termPattern.test(term) }
function trimTerm(term) { return term.trim().replace(/^[“”"‘’'(),.;:!?]+|[“”"‘’'(),.;:!?]+$/g, '').trim() }
function open(term, context = '') {
  message.value = ''
  term = trimTerm(term)
  if (!validTerm(term)) { message.value = 'Chọn một từ hoặc cụm tiếng Anh, tối đa 12 từ và 100 ký tự.'; return }
  selected.value = null
  popup.value.open({term, context, lessonId: props.lesson.id, lessonTitle: props.lesson.title})
}
function contextFor(node, offset = 0) {
  const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
  const container = element.closest('p, li, td, blockquote, h3') || element
  const text = container.textContent
  const prefix = document.createRange()
  prefix.selectNodeContents(container)
  prefix.setEnd(node, offset)
  const position = prefix.toString().length
  const sentence = [...text.matchAll(/[^.!?\n]+[.!?]?/g)].find(match => match.index <= position && match.index + match[0].length > position)
  return (sentence?.[0] || text).trim().slice(0, 1000)
}
function selectionChanged() {
  const selection = window.getSelection()
  if (!boundTarget || !selection?.rangeCount || selection.isCollapsed || !boundTarget.contains(selection.anchorNode) || !boundTarget.contains(selection.focusNode)) { selected.value = null; return }
  const parent = selection.anchorNode.nodeType === Node.TEXT_NODE ? selection.anchorNode.parentElement : selection.anchorNode
  if (parent.closest('input, textarea, [data-no-lookup]')) { selected.value = null; return }
  const term = trimTerm(selection.toString())
  if (!validTerm(term)) { selected.value = null; return }
  const range = selection.getRangeAt(0)
  const box = range.getBoundingClientRect()
  selected.value = {term, context: contextFor(range.startContainer, range.startOffset)}
  selectionStyle.value = {left: `${Math.max(12, Math.min(box.left, window.innerWidth - 220))}px`, top: `${Math.max(8, Math.min(box.bottom + 8, window.innerHeight - 60))}px`}
}
function clicked(event) {
  if (event.button !== 0 || event.target.closest('[data-no-lookup]')) return
  const card = event.target.closest('[data-lookup-term]')
  if (card) { open(card.dataset.lookupTerm); return }
  if (event.target.closest('button, a, input, textarea, select, summary, label, [role="button"]')) return
  if (window.getSelection()?.toString().trim()) { selectionChanged(); return }
  const caret = document.caretPositionFromPoint?.(event.clientX, event.clientY)
  const fallback = !caret && document.caretRangeFromPoint?.(event.clientX, event.clientY)
  const node = caret?.offsetNode || fallback?.startContainer
  let offset = caret?.offset ?? fallback?.startOffset
  if (!node || node.nodeType !== Node.TEXT_NODE || !boundTarget.contains(node)) return
  const text = node.textContent
  if (offset === text.length || !/[a-z]/i.test(text[offset] || '')) offset--
  const token = [...text.matchAll(/[a-z]+(?:['’-][a-z]+)*/gi)].find(match => match.index <= offset && match.index + match[0].length > offset)
  if (!token) return
  // Do not treat fragments of a Vietnamese word as an English word.
  const nonEnglishLetter = character => /[\p{L}\p{M}]/u.test(character || '') && !/[a-z]/i.test(character || '')
  if (nonEnglishLetter(text[token.index - 1]) || nonEnglishLetter(text[token.index + token[0].length])) return
  const range = document.createRange()
  range.setStart(node, token.index); range.setEnd(node, token.index + token[0].length)
  const rect = range.getBoundingClientRect()
  if (event.clientX < rect.left - 2 || event.clientX > rect.right + 2 || event.clientY < rect.top - 2 || event.clientY > rect.bottom + 2) return
  open(token[0], contextFor(node, token.index))
}
function bind(target) {
  if (boundTarget) boundTarget.removeEventListener('click', clicked)
  boundTarget = target
  target?.addEventListener('click', clicked)
}
function hideSelection() { selected.value = null }
watch(() => props.target, bind, {immediate: true})
watch(() => props.lesson.id, () => { popup.value?.close(); selected.value = null; query.value = '' })
onMounted(() => { document.addEventListener('selectionchange', selectionChanged); window.addEventListener('scroll', hideSelection, true) })
onUnmounted(() => { bind(null); document.removeEventListener('selectionchange', selectionChanged); window.removeEventListener('scroll', hideSelection, true) })
</script>

<template>
  <section class="english-vocabulary-tools" data-no-lookup>
    <div class="lookup-intro"><strong>Từ mới? Tra ngay trong bài.</strong><p>Chạm một từ tiếng Anh; bôi đen hoặc giữ để chọn cụm từ rồi bấm Tra cụm.</p></div>
    <form class="lookup-form" @submit.prevent="open(query)"><input v-model="query" class="form-control" aria-label="Từ hoặc cụm tiếng Anh cần tra" placeholder="Nhập từ hoặc cụm từ…" maxlength="100" required><button class="btn btn-light" type="submit">Tra từ</button></form>
    <RouterLink v-if="store.user?.role === 'student'" :to="{path:'/tu-vung', query:{lesson_id:lesson.id}}" class="lookup-bank-link">Từ em đã lưu trong bài →</RouterLink>
    <p v-if="message" class="lookup-error" role="alert">{{ message }}</p>
  </section>
  <Teleport to="body"><button v-if="selected" type="button" class="lookup-selection" :style="selectionStyle" @pointerdown.prevent @click="open(selected.term, selected.context)">Tra cụm: <b>{{ selected.term }}</b> ↗</button></Teleport>
  <VocabularyDialog ref="popup" />
</template>

<style scoped>
.english-vocabulary-tools { margin: 20px 25px 0; padding: 18px; border-radius: 15px; border: 1px solid #e4dcf3; background: #faf7ff; }
.lookup-intro strong { color: #68508f; font-size: 14px; }
.lookup-intro p { margin: 5px 0 12px; color: #818099; font-size: 12px; line-height: 1.7; }
.lookup-form { display: flex; gap: 8px; }
.lookup-form input { min-width: 0; height: 42px; background: white; }
.lookup-form button { flex-shrink: 0; }
.lookup-bank-link { display: inline-block; margin-top: 11px; color: #76608e; font-size: 12px; }
.lookup-error { font-size: 12px; color: #9c4b39; margin: 10px 0 0; }
.lookup-selection { position: fixed; z-index: 40; max-width: min(310px, calc(100vw - 24px)); padding: 12px 16px; border: 1px solid #ded5f0; border-radius: 13px; box-shadow: 0 7px 30px #40335726; color: #61458b; background: white; cursor: pointer; overflow-wrap: anywhere; font-size: 13px; }
@media(max-width: 560px) { .english-vocabulary-tools { margin: 16px 16px 0; padding: 14px; } }
</style>
